// Shared CRM form-tracking helper for Scotty's Land Specialist.
// Used by the hero form, the inline embedded form, and the quote modal.

type StandardTrackingFieldKey = string;
type RegisteredCustomFieldId = string;
type TrackingCustomField = { value?: unknown; label: string };

export const TRACKING_ID = "tk_f096f18000134991b3d38abcca3f619d";
export const LOCATION_ID = "5FMGxd2SbzRDVO0B4g5P";
export const PROJECT_ID = "1792678768838541728";

// Custom field IDs registered in the CRM for this project's quote forms.
export const CUSTOM_FIELD_SERVICE = "ZSjXO3qNQHNIdrgDRcTH"; // Service Needed
export const CUSTOM_FIELD_DETAILS = "M6BFm8B3IW1Rril8koRX"; // Project Details
export const CUSTOM_FIELD_SERVICE_WANTED = "pq1KbYctTzi8u6rhtnlF"; // Service Wanted ({{contact.service_wanted}})

export const postTrackingEvent = (
  trackingPayload: Record<string, unknown> & {
    formData: Record<StandardTrackingFieldKey, unknown>;
    formLabels: Record<StandardTrackingFieldKey, string>;
  },
  options: {
    customFields?: Record<RegisteredCustomFieldId, TrackingCustomField>;
  } = {},
) => {
  const { customFields = {} } = options;
  const eventPayload = {
    ...trackingPayload,
    formData: { ...trackingPayload.formData },
    formLabels: { ...trackingPayload.formLabels },
  };

  for (const [key, field] of Object.entries(customFields)) {
    if (field.value === undefined || field.value === "") continue;
    eventPayload.formData[key] = field.value;
    eventPayload.formLabels[key] = field.label;
  }

  for (const key of Object.keys(eventPayload.formData)) {
    eventPayload.formLabels[key] ||= key;
  }

  const body = new FormData();
  body.append("event", JSON.stringify(eventPayload));

  fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
    method: "POST",
    headers: { version: "2021-07-28" },
    body,
  }).catch(() => {});
};

export const getTrackingSessionId = () => {
  try {
    const sid = document.cookie.match(
      /(?:^|;\s*)__vibe_sid=([0-9a-f-]{36})(?:;|$)/,
    )?.[1];
    if (sid) return sid;
  } catch {}
  return crypto.randomUUID();
};

export interface QuoteFormValues {
  name: string;
  email?: string;
  phone?: string;
  service: string;
  location?: string;
  details?: string;
}

// Build a standard tracking payload from a quote form submission.
export const buildQuoteTrackingPayload = (
  formId: string,
  formName: string,
  values: QuoteFormValues,
) => {
  const nameParts = values.name.trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return {
    type: "external_form_submission",
    timestamp: Date.now(),
    formId,
    formData: {
      first_name: firstName,
      last_name: lastName,
      ...(values.email ? { email: values.email } : {}),
      ...(values.phone ? { phone: values.phone } : {}),
      ...(values.location ? { city: values.location } : {}),
    },
    formLabels: {
      first_name: "First Name",
      last_name: "Last Name",
      email: "Email Address",
      phone: "Phone",
      city: "City / Location",
    },
    url: typeof window !== "undefined" ? window.location.href : "",
    title: typeof document !== "undefined" ? document.title : "",
    path: typeof window !== "undefined" ? window.location.pathname : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    trackingId: TRACKING_ID,
    locationId: LOCATION_ID,
    projectId: PROJECT_ID,
    sessionId: getTrackingSessionId(),
    properties: {
      deviceType:
        typeof navigator !== "undefined" &&
        /Mobile|Android|iPhone/i.test(navigator.userAgent)
          ? "mobile"
          : "desktop",
      source: "ai_studio",
      projectId: PROJECT_ID,
      formName,
    },
  };
};
